// Split out of src/components/SettingsTab.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// SettingsTab.jsx re-exports everything it exported before, so existing imports keep working.

import { CoinIcon, HintTip, Icon, VaultIcon } from "../Common.jsx";
import { coachEnabled } from "../../lib/coachEnabled.js";
import { CURRENCY_SYMBOL, MAX_EXCHANGE_RATE, tokenValueParts, uid } from "../../lib/constants.js";
import { getDeviceId } from "../../lib/api.js";
import { fmtGBP, fmtPence, logSetting, rateMarkerTxn, rescaleHouseholdRewards, todayStr, weeklyCapLine, weekStartOf } from "../../lib/utils.js";
import { applyRateChange } from "../../lib/rateChange.js";
import { RateChangeFlow, tokenValueLabel } from "../RateChange.jsx";
import { MAX_VAULT_INTEREST_APR, STALE_VAULT_DAYS, VAULT_INTEREST_APR_PRESETS, clampApr, nextMondayPayoutPence, staleVaultChildren, weeklyInterestExactPence, yearlyInterestPence } from "../../lib/vaultInterest.js";
import { t as ct } from "../../lib/content.js";
import { actor, AttentionDot } from "./shared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Shared by SettingsTab and the wizard's TokenSetupStep so the exchange-rate input and the
// "show £ value" toggle stay in sync everywhere they appear, rather than forking the logic.
// seedDefaults (true only from the wizard) is what gates the reward-rescaling behaviour
// below — see saveExchangeRate.

// Preset token values (Alan, Aug 2026). Presented as "what is one token worth?" rather than
// a raw tokens-per-£1 box, because that's the question a parent can actually answer — the
// old field asked them to reason backwards from it.
//
// Every preset divides 100 exactly, so its per-token value is precise. All sit at or below
// MAX_EXCHANGE_RATE. "Other" reveals the numeric field for anything else, and is also where
// a legacy household on an off-preset rate (3, 7, 12...) lands: their rate is preserved and
// never auto-snapped, since snapping would change a deliberate choice unasked.
const TOKEN_VALUE_PRESETS = [
  { rate: 20, label: "5p" },
  { rate: 10, label: "10p" },
  { rate: 5, label: "20p" },
  { rate: 4, label: "25p" },
];

// `compact` (wizard money step, Option A — Alan, 4 Sep): the question becomes the card
// heading and the two explanatory paragraphs are dropped. The wizard shows the relationship
// the hint used to describe as a live summary line instead; Settings keeps the full copy.
export function TokenSetupFields({ state, update, showToast, seedDefaults, confirmRescale, onSpendingReview, compact = false }) {
  const storedRate = state.settings.exchangeRate ?? 10;
  const [exchangeRate, setExchangeRate] = useState(String(storedRate));
  // "Other" stays selected for any rate that isn't one of the presets — including a legacy
  // household's existing value, which is exactly the point: it gives off-preset rates a home
  // rather than making them a display-only special case.
  const [showCustom, setShowCustom] = useState(!TOKEN_VALUE_PRESETS.some((p) => p.rate === storedRate));

  function applyRate(rate, { fromPreset } = {}) {
    if (!rate || rate <= 0) { showToast(ct("toast.exchange_rate_invalid", "Exchange rate must be a positive number"), "bad"); return; }
    if (rate > MAX_EXCHANGE_RATE) {
      showToast(ct("toast.exchange_rate_too_high", "Keep it to {max} tokens or fewer per {symbol}1 — more than that and balances get unwieldy", { max: MAX_EXCHANGE_RATE, symbol: CURRENCY_SYMBOL }), "bad");
      return;
    }
    // Rescaling only ever fires from the wizard's TokenSetupStep (seedDefaults), and only
    // there — it's meant to give sensible STARTING reward numbers once, matched to the rate
    // a parent picks before they've touched a single reward value (the wizard sets the
    // exchange rate on step 3, habits/activities on step 4). A parent changing the rate
    // later from Settings has, by definition, already tuned some of these values — silently
    // rewriting them out from under a deliberate choice would be a real trust breach, so
    // that path is a plain rate change and nothing else (Alan, Aug 2026).
    if (seedDefaults) {
      const oldRate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
      const rescaled = rate !== oldRate;
      // confirmRescale is passed by the wizard's TokenSetupStep only when the parent is
      // RETURNING to the rate step, i.e. they may already have tuned reward values on a
      // later step. In that case the rescale stops being an invisible setup convenience and
      // becomes something that rewrites their work, so it needs consent (Alan, Aug 2026).
      // First forward pass keeps the silent rescale: nothing has been tuned yet, so there's
      // nothing to consent to and a prompt would just be noise in a flow that should be fast.
      if (rescaled && confirmRescale) {
        confirmRescale({
          oldRate,
          newRate: rate,
          onAccept: () => {
            update((s) => { logSetting(s, getDeviceId(), "exchangeRate", `${s.settings.exchangeRate} = £1`, `${rate} = £1`, actor(s)); s.transactions.unshift(rateMarkerTxn(getDeviceId(), s.settings.exchangeRate, rate)); s.settings.exchangeRate = rate; rescaleHouseholdRewards(s, rate / oldRate); return s; });
            showToast(ct("toast.exchange_rate_set_rescaled", "Exchange rate set. Reward amounts adjusted to match"), "good");
          },
          onDecline: () => {
            // Declining still changes the RATE — it only skips the rescale.
            update((s) => { logSetting(s, getDeviceId(), "exchangeRate", `${s.settings.exchangeRate} = £1`, `${rate} = £1`, actor(s)); s.transactions.unshift(rateMarkerTxn(getDeviceId(), s.settings.exchangeRate, rate)); s.settings.exchangeRate = rate; return s; });
            showToast(ct("toast.exchange_rate_set_kept", "Exchange rate set. Reward amounts left as they are"), "good");
          },
        });
        return;
      }
      update((s) => {
        logSetting(s, getDeviceId(), "exchangeRate", `${s.settings.exchangeRate} = £1`, `${rate} = £1`, actor(s)); s.transactions.unshift(rateMarkerTxn(getDeviceId(), s.settings.exchangeRate, rate));
        s.settings.exchangeRate = rate;
        if (rescaled) rescaleHouseholdRewards(s, rate / oldRate);
        return s;
      });
      showToast(rescaled ? ct("toast.exchange_rate_set_rescaled", "Exchange rate set. Starting reward amounts adjusted to match") : ct("toast.exchange_rate_set", "Exchange rate set"), "good");
      return;
    }
    // Settings never reaches here any more (LB-355): a live household's change goes through
    // RateChangeFlow and applyLiveRateChange below. Kept as the plain path for any other
    // caller that is not the wizard.
    update((s) => { logSetting(s, getDeviceId(), "exchangeRate", `${s.settings.exchangeRate} = £1`, `${rate} = £1`, actor(s)); s.transactions.unshift(rateMarkerTxn(getDeviceId(), s.settings.exchangeRate, rate)); s.settings.exchangeRate = rate; return s; });
    if (!fromPreset) showToast(ct("toast.exchange_rate_updated", "Exchange rate updated"), "good");
  }

  // LB-355 (Alan, 20–21 Sep). A live household's rate change asks two questions — what
  // happens to the tokens children already have, and to rewards from now on — in the
  // three-step RateChangeFlow, and applies both answers in one update through
  // lib/rateChange.js. It replaces the LB-83 offer that followed a change and multiplied
  // every reward by the rate ratio: Rebalance sizes rewards to each child's cap instead,
  // which is the same answer where rewards already fitted and the right one where they
  // did not (Alan's own household, reaching the cap in two days).
  function applyLiveRateChange(toRate, { balances, rewards }) {
    let result = null;
    update((s) => {
      result = applyRateChange(s, {
        toRate, balances, rewards,
        deviceId: getDeviceId(), acting: actor(s), newId: uid, nowIso: new Date().toISOString(),
        resizeReason: ct("settings.rate_change.txn_reason", "Token value changed to {to}", { to: tokenValueLabel(toRate) }),
      });
      return result.changed ? s : null;
    });
    setPendingRate(null);
    setExchangeRate(String(toRate));
    setShowCustom(!TOKEN_VALUE_PRESETS.some((p) => p.rate === toRate));
    if (!result || !result.changed) return;
    const over = (state.profiles || []).filter((p) => result.rebalanced[p.id] && result.rebalanced[p.id].fits === false).map((p) => p.name);
    showToast(over.length
      ? ct("toast.rate_changed_over", "Token value changed to {to}. {names} still over the weekly cap: see Earnings check.", { to: tokenValueLabel(toRate), names: over.join(", ") })
      : ct("toast.rate_changed", "Token value changed to {to}", { to: tokenValueLabel(toRate) }), over.length ? "info" : "good");
  }

  // Q6 resolution (Alan, 27 Aug): in SETTINGS, changing the token value is no longer
  // frictionless — selecting a different value opens an explicit confirm stating the
  // implications (reward token-amounts stay put so their £ value shifts; balances'
  // £-value shifts; the vault, in pounds, does not move; nothing rescales). Accept
  // stores the change; Cancel stores nothing. The wizard keeps its existing behaviour
  // (silent first-pass rescale / returning-visit confirmRescale) — this gate is for
  // live households only.
  const [pendingRate, setPendingRate] = useState(null);
  // The APPLIED rate — distinct from activeRate, which follows the input draft. The
  // highlight, the no-op check, and the confirm's "old" side must all read what's
  // actually stored, not what's being typed.
  const appliedRate = (!seedDefaults && state.settings.exchangeRate > 0) ? state.settings.exchangeRate : null;
  function pickPreset(rate) {
    setShowCustom(false);
    if (!seedDefaults) {
      // Settings: the draft doesn't move until the change is ACCEPTED — the highlight
      // stays on the stored value throughout the confirm.
      if (rate === appliedRate) { setPendingRate(null); return; }
      setPendingRate(rate);
      return;
    }
    setExchangeRate(String(rate));
    applyRate(rate, { fromPreset: true });
  }
  function requestCustomRate() {
    const parsed = parseInt(exchangeRate, 10);
    if (!parsed || parsed <= 0) { showToast(ct("toast.exchange_rate_invalid", "Exchange rate must be a positive number"), "bad"); return; }
    if (parsed > MAX_EXCHANGE_RATE) { showToast(ct("toast.exchange_rate_too_high", "Keep it to {max} tokens or fewer per {symbol}1 — more than that and balances get unwieldy", { max: MAX_EXCHANGE_RATE, symbol: CURRENCY_SYMBOL }), "bad"); return; }
    if (parsed === appliedRate) { setPendingRate(null); return; }
    setPendingRate(parsed);
  }

  const activeRate = parseInt(exchangeRate, 10) || 10;
  const { pence, exact } = tokenValueParts(activeRate);

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="token-value-card">
      {compact ? (
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0"><CoinIcon className="w-5 h-5" /></div>
          <div className="text-sm font-bold text-slate-900">{ct("wizard.money.token_heading", "One token is worth")}</div>
        </div>
      ) : (
        <>
          {/* LB-265 (Alan, 12 Sep): "needs to be more concise". Two paragraphs of intro plus
              two footer lines was four blocks of prose before the parent reached a control.
              One line states the consequence; the vault reassurance — still a real question,
              just not one that needs answering on every visit — moves behind the ⓘ, and is
              repeated in full inside the change confirmation, which is where it matters. */}
          {/* The heading's words get their own <span>: with the ⓘ as a sibling text node in
              the same element, the line's text content became "What's one token worth?i",
              which is not what the heading says. Same treatment on the vault card below. */}
          <div className="text-sm font-semibold text-slate-700 mb-1 flex items-center">
            <span>{ct("settings.token_value_question", "What's one token worth?")}</span>
            <HintTip text={ct("settings.rate_vault_note", "Savings Vault money is kept in pounds, so changing the token value never touches it. When you change it, you choose what happens to balances and rewards.")} />
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {ct("settings.token_value_hint", "Every number in the app scales with this. Kids see the {symbol} value beside their balances.", { symbol: CURRENCY_SYMBOL })}
          </p>
        </>
      )}
      <div className="grid grid-cols-5 gap-1.5" data-testid="token-value-presets">
        {TOKEN_VALUE_PRESETS.map((p) => {
          const selected = !showCustom && (seedDefaults ? activeRate : appliedRate) === p.rate;
          return (
            <button
              key={p.rate}
              type="button"
              data-testid={`token-preset-${p.rate}`}
              aria-pressed={selected}
              onClick={() => pickPreset(p.rate)}
              className={`flex-1 rounded-lg py-2 px-1 border-2 transition-colors ${selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`}
            >
              <div className={`text-sm font-bold leading-none ${selected ? "text-white" : "text-slate-900"}`}>{p.label}</div>
              <div className={`text-xs font-medium mt-1 ${selected ? "text-white/70" : "text-slate-400"}`}>{p.rate} = {CURRENCY_SYMBOL}1</div>
            </button>
          );
        })}
        <button
          type="button"
          data-testid="token-preset-other"
          aria-pressed={showCustom}
          onClick={() => setShowCustom(true)}
          className={`flex-1 rounded-xl py-2 px-1 border-2 transition-colors ${showCustom ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white"}`}
        >
          <div className={`text-sm font-bold leading-none ${showCustom ? "text-teal-700" : "text-slate-900"}`}>{ct("settings.token_value_other", "Other")}</div>
          <div className="text-xs font-medium text-slate-400 mt-1">{ct("settings.token_value_other_sub", "set it")}</div>
        </button>
      </div>

      {showCustom && (
        <div className="flex items-center gap-2 mt-3">
          <input
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-24 text-center"
            type="number" min="1" max={MAX_EXCHANGE_RATE}
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value.replace(/\D/g, ""))}
          />
          <span className="text-xs text-slate-400">tokens = {CURRENCY_SYMBOL}1</span>
          {/* 3.2 rate preview (P-decision): make the change CONCRETE before it's applied —
              the same child's tokens at the current vs proposed value, plus the standing
              reassurance that vault money is in pounds and can't move (the invariant W4's
              regression test pins). Reads from the draft, so it live-updates per keystroke. */}
          {(() => {
            const draft = parseInt(exchangeRate, 10);
            if (!draft || draft <= 0 || draft > MAX_EXCHANGE_RATE || draft === (state.settings.exchangeRate || 10)) return null;
            const kid = (state.profiles || [])[0];
            if (!kid) return null;
            const cur = state.settings.exchangeRate || 10;
            return (
              <p className="text-xs text-slate-500 mt-1.5" data-testid="rate-preview">
                {ct("settings.rate_preview", "At {draft} = £1: {name}'s {tokens} tokens = {newVal} (currently {oldVal}).", { draft, name: kid.name, tokens: kid.tokenBalance, newVal: fmtGBP(kid.tokenBalance || 0, draft), oldVal: fmtGBP(kid.tokenBalance || 0, cur) })}
              </p>
            );
          })()}
          <button className="ml-auto bg-slate-800 text-white font-bold text-xs rounded-lg px-3 py-2" onClick={() => (seedDefaults ? applyRate(parseInt(exchangeRate, 10)) : requestCustomRate())}>{ct("settings.save_button", "Save")}</button>
        </div>
      )}

      {/* LB-355: a different value opens the three-step change (signed off 21 Sep). Cancel
          stores nothing; confirming applies both answers at once. */}
      {pendingRate !== null && !seedDefaults && (
        <RateChangeFlow
          state={state}
          toRate={pendingRate}
          onCancel={() => { setPendingRate(null); setExchangeRate(String(appliedRate || 10)); setShowCustom(!TOKEN_VALUE_PRESETS.some((p) => p.rate === (appliedRate || 10))); }}
          onConfirm={(answers) => applyLiveRateChange(pendingRate, answers)}
        />
      )}

      <div className="mt-3 pt-2.5 border-t border-slate-100">
        <div className="text-sm font-semibold text-slate-700" data-testid="token-value-line">
          {ct("settings.token_value_line", "1 token {approx} {pence}p", { approx: exact ? "=" : "≈", pence })}
          <span className="text-slate-400 font-semibold"> ({fmtGBP(1, activeRate)})</span>
        </div>
        <div className="text-xs text-slate-400 mt-1" data-testid="token-value-context">
          {/* LB-324: on the wizard money step (compact) the line describes the cap the parent
              has chosen, recomputed when either control changes. With "a cap each" there is no
              single cap, so that state (and Settings, which has no cap control) keeps the
              rate-only sentence. */}
          {compact && !(state.settings.capMode === "perChild" && (state.profiles || []).length > 1)
            ? weeklyCapLine(activeRate, (state.profiles || [])[0]?.spendingCap?.weeklyGBP)
            : ct("settings.token_value_context", "{symbol}5 of pocket money a week ≈ {tokens} tokens.", { symbol: CURRENCY_SYMBOL, tokens: activeRate * 5 })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ vault interest ------------------------------ */

// Household-level Vault Interest ("Money Monday" — docs/VAULT_INTEREST_SCOPE.md), living
// beside the exchange rate as the second entry in Settings -> Rewards & Currency: one
// yearly rate for the whole household (per-child rates deliberately out of v1 — one rate
// avoids sibling disputes, and per-child opt-out already exists via each child's own
// Savings Vault toggle on the Kids tab). Enabling (re)sets every child's anchor to the
// current week, so a period spent switched off is never back-paid.
// LB-19: the household Adaptive switch (docs/ADAPTIVE_LE_SCOPE.md §1, §9). On by default —
// premium, free during the beta. Off = every Learn & Earn draw exactly as before Adaptive;
// nothing a child has earned is removed. Alan, 12 Sep: "where is the household adaptive switch?"
export function AdaptiveCard({ state, update, showToast }) {
  const on = coachEnabled(state.settings);
  function toggle() {
    update((s) => { logSetting(s, getDeviceId(), "adaptive", on ? "on" : "off", on ? "off" : "on", actor(s)); s.settings.adaptive = !on; return s; });
    showToast(on ? ct("toast.adaptive_off", "Learning Coach is off. Games use the school-year lists as before.") : ct("toast.adaptive_on", "Learning Coach is on for every child."), "good");
  }
  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="adaptive-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Icon id="ic-brain" size={16} className="inline-block align-[-3px]" />{ct("settings.adaptive.title", "Learning Coach")}<span className="text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">{ct("adaptive.row.premium", "Premium")}</span><span className="text-xs text-slate-500 font-normal">{ct("adaptive.row.beta", "free in the Preview")}</span></div>
          <p className="text-xs text-slate-500 mt-0.5">{ct("settings.adaptive.body", "Questions start easy inside each child's school year and get harder as they get better; a game moves up a level when they've earned it, and you're told. Off means the school-year lists, as before. Badges and mastery are kept either way.")}</p>
        </div>
        <button type="button" role="switch" aria-checked={on} aria-label={ct("settings.adaptive.switch_label", "Learning Coach on or off")} onClick={toggle}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
        </button>
      </div>
    </div>
  );
}

// LB-248: household opt-out for the monthly double token day (on by default — Alan, 9 Sep).
export function DoubleTokenDayCard({ state, update, showToast }) {
  const on = state.settings?.doubleTokenDay !== false;
  function toggle() {
    update((s) => { logSetting(s, getDeviceId(), "doubleTokenDay", on ? "on" : "off", on ? "off" : "on", actor(s)); s.settings.doubleTokenDay = !on; return s; });
    showToast(on ? ct("toast.double_day_off", "Double token day is off") : ct("toast.double_day_on", "Double token day is on, once a month per child"), "good");
  }
  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="double-day-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-700"><Icon id="ic-celebrate" size={16} className="inline-block align-[-3px] mr-1.5" />{ct("settings.double_day.title", "Double token day")}</div>
          <p className="text-xs text-slate-500 mt-0.5">{ct("settings.double_day.body", "One day a month, each child earns double from tasks and Learn & Earn. The day is picked at random and is different for each child, so nobody can plan around it. The weekly cap still applies, and off means it never happens.")}</p>
        </div>
        <button type="button" role="switch" aria-checked={on} aria-label={ct("settings.double_day.switch_label", "Double token day on or off")} onClick={toggle}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
        </button>
      </div>
    </div>
  );
}

// LB-265 (Alan, 12 Sep): the card used to render fully open on every visit — scope note,
// rate picker, per-child forecast and two mechanics paragraphs — so a parent who set the
// rate in August got the whole setup screen again every time they walked past. It now
// follows the hierarchy he set: is it on, what does it mean, what is it set to, what does
// it cost. The last two are rows that expand.
//
// Two deliberate defaults from that round: the RATE row starts open (a household that has
// just switched interest on has never chosen a rate, and collapsing the chips would leave
// them on the 10% default without ever being shown it), and changing the rate AUTO-OPENS
// the cost row, because "what does this now cost me" is the only question a rate change
// raises. Warnings are not detail and never collapse.
function InterestDetailRow({ id, label, value, open, onToggle, children }) {
  return (
    <>
      <button
        type="button"
        data-testid={id}
        aria-expanded={open}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 border-t border-slate-100 py-2.5 text-left"
      >
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-sm font-bold text-slate-900">{value}</span>
          <Icon id={open ? "ic-chevron-up" : "ic-chevron-down"} size={16} className="text-slate-400" style={{ color: "currentColor" }} />
        </span>
      </button>
      {open && <div className="pb-1">{children}</div>}
    </>
  );
}

export function VaultInterestCard({ state, update, showToast }) {
  const vi = state.settings.vaultInterest || { enabled: false, apr: 10 };
  const apr = clampApr(vi.apr);
  const isPreset = VAULT_INTEREST_APR_PRESETS.includes(apr);
  const [customDraft, setCustomDraft] = useState(() => (isPreset ? "" : String(apr)));
  const [rateOpen, setRateOpen] = useState(true);
  const [costOpen, setCostOpen] = useState(false);

  function setEnabled(on) {
    update((s) => {
      if (!s.settings.vaultInterest || typeof s.settings.vaultInterest !== "object") s.settings.vaultInterest = { enabled: false, apr: 10 };
      logSetting(s, getDeviceId(), "vaultInterest.enabled", s.settings.vaultInterest.enabled ? "on" : "off", on ? "on" : "off", actor(s));
      s.settings.vaultInterest.enabled = on;
      if (on) {
        // Start (or restart) the clock at the current week for everyone — first payment
        // lands next Monday, nothing retroactive, carries reset.
        const wk = weekStartOf(todayStr());
        s.profiles.forEach((p) => { p.vaultInterestAnchor = wk; p.vaultInterestCarry = 0; });
      }
      return s;
    });
    // Switching on lands on the rate question rather than a collapsed summary of a rate
    // nobody has chosen yet.
    if (on) { setRateOpen(true); setCostOpen(false); }
    showToast(
      on
        ? ct("toast.vault_interest_on", "Vault interest is on. The first payment lands next Monday")
        : ct("toast.vault_interest_off", "Vault interest switched off"),
      on ? "good" : "info"
    );
  }

  function saveApr(next) {
    const clamped = clampApr(next);
    if (clamped === apr) return;
    update((s) => {
      if (!s.settings.vaultInterest || typeof s.settings.vaultInterest !== "object") s.settings.vaultInterest = { enabled: vi.enabled, apr: 10 };
      if (s.settings.vaultInterest.apr !== clamped) logSetting(s, getDeviceId(), "vaultInterest.apr", `${s.settings.vaultInterest.apr}%`, `${clamped}%`, actor(s));
      s.settings.vaultInterest.apr = clamped;
      return s;
    });
    setCostOpen(true); // Alan, LB-265: a rate change shows what it now costs, unasked.
    showToast(ct("toast.vault_interest_rate", "Vault interest set to {apr}% a year", { apr: clamped }), "good");
  }

  // Per-child interest opt-out (Alan, Aug 2026) — was bankEnabled(), which read the old
  // vault on/off flag. Missing field reads as ON, matching migrateState's default.
  const earnsVaultInterest = (p) => p?.earnsVaultInterest !== false;
  const eligible = state.profiles.filter(earnsVaultInterest);
  // Derived, NOT stored (Alan chose option (a), Aug 2026). When every child is opted out the
  // household switch must stop reading as "on" — but the fix is presentational only. Writing
  // settings.vaultInterest.enabled = false here instead would silently mutate a setting the
  // parent never touched, and because setEnabled(true) resets EVERY child's anchor and carry,
  // switching it back on later would then have a side effect on children who were never
  // opted out. Deriving it costs one boolean and has neither problem.
  const nobodyEarning = state.profiles.length > 0 && eligible.length === 0;
  const effectivelyOn = !!vi.enabled && !nobodyEarning;
  const weeklyTotal = eligible.reduce((sum, p) => sum + nextMondayPayoutPence(p, apr), 0);
  const yearlyTotal = eligible.reduce((sum, p) => sum + yearlyInterestPence(p.vaultBalancePence, apr), 0);

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="vault-interest-card">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <VaultIcon className="w-4 h-4 inline-block" /> <span>{ct("settings.vault_interest_title", "Vault interest")}</span>
          {/* The household-vs-child scope question, still answered, no longer answered at
              every parent on every visit. Replaces an ℹ️ emoji the sprite passes missed. */}
          <HintTip text={ct("vault_interest.scope_note", "This turns interest on or off for the whole household. To stop it for just one child, use their own interest toggle in Kids instead, not this switch.")} />
        </div>
        <button type="button" data-testid="vault-interest-toggle" onClick={() => setEnabled(!vi.enabled)} aria-pressed={effectivelyOn} aria-label={vi.enabled ? "Turn vault interest off" : "Turn vault interest on"}
          className={`flex-shrink-0 inline-block w-11 h-6 rounded-full relative transition-colors ${effectivelyOn ? "bg-emerald-500" : "bg-slate-200"}`}>
          <span className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${vi.enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-1">{ct("vault_interest.card_sub", "Savings grow a little every Monday. The kids call it Money Monday.")}</p>
      {/* P3 trigger (b) — the counterfactual Money Monday prompt: shown only while
          interest is OFF and there's real vault money it isn't growing. Concrete and
          per-child ("Alex would have earned 6p") beats abstract ("interest is available")
          — it's the same number Money Monday would have printed. */}
      {!vi.enabled && (() => {
        const withVault = (state.profiles || []).filter((p) => (p.vaultBalancePence || 0) > 0);
        if (withVault.length === 0) return null;
        const top = withVault.reduce((a, b) => ((a.vaultBalancePence || 0) >= (b.vaultBalancePence || 0) ? a : b));
        const wouldEarn = Math.round(weeklyInterestExactPence(top.vaultBalancePence, apr || 10));
        if (wouldEarn < 1) return null;
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2 mb-1" data-testid="interest-counterfactual">
            <p className="text-xs text-amber-700 font-semibold">{ct("vault_interest.counterfactual", "Money Monday came and went — with interest on at {apr}%, {name} would have earned {amount} this week. Right now their savings aren't growing.", { apr: apr || 10, name: top.name, amount: fmtPence(wouldEarn) })}</p>
          </div>
        );
      })()}
      {/* Attention, not detail: both of these stay on the face of the card whenever they
          apply, whatever the rows below are doing. */}
      {nobodyEarning && vi.enabled && (
        <p className="text-xs font-semibold text-amber-600 mt-2 mb-1 flex items-start gap-1" data-testid="vault-interest-nobody-earning">
          <span className="flex-shrink-0"><Icon id="ic-attention" size={16} hue="amber" /></span>
          <span>{ct("settings.vault_interest_none_note", "No children are set to earn interest, so nothing is being paid. Turn it on for a child in Kids.")}</span>
        </p>
      )}
      {vi.enabled && (() => {
        const stale = staleVaultChildren(state);
        if (stale.length === 0) return null;
        const names = stale.map((p) => p.name).join(", ");
        return (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mt-2 mb-1 flex items-start gap-1.5">
            <AttentionDot />
            <p className="text-xs text-amber-800 leading-relaxed">{ct("vault_interest.stale_note", "{names}’s vault hasn’t changed in over {weeks} weeks — still right? You’re paying interest on that balance, so it’s worth a quick check against the real world. Update it from Kids → Balances or Rewards → Manage balances.", { names, weeks: Math.round(STALE_VAULT_DAYS / 7) })}</p>
          </div>
        );
      })()}
      {vi.enabled && (
        <div className="mt-2">
          <InterestDetailRow
            id="vault-interest-rate-row"
            label={ct("vault_interest.rate_label", "Yearly rate")}
            value={`${apr}%`}
            open={rateOpen}
            onToggle={() => setRateOpen((v) => !v)}
          >
            <div className="flex gap-1.5 mb-1.5">
              {VAULT_INTEREST_APR_PRESETS.map((r) => (
                <button key={r} type="button" onClick={() => { setCustomDraft(""); saveApr(r); }}
                  className={`flex-1 rounded-lg text-sm font-bold py-2 ${apr === r && customDraft === "" ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-500"}`}>{r}%</button>
              ))}
              <input
                type="number" min="1" max={MAX_VAULT_INTEREST_APR} placeholder="…"
                aria-label="Custom yearly rate"
                className={`w-16 rounded-lg border text-sm font-bold text-center ${customDraft !== "" ? "border-teal-400 text-teal-600" : "border-slate-200 text-slate-500"}`}
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value.replace(/\D/g, ""))}
                onBlur={() => { if (customDraft !== "") saveApr(parseInt(customDraft, 10)); }}
              />
            </div>
            {/* The comparison that used to sit here ("pocket-money parents average ~9%") was an
                invented statistic: no source, nothing measures a pocket-money interest rate, and
                it was sitting on the screen where a parent picks a rate. Removed 13 Sep. LB-276
                is the honest version: the real median across households actually using this. */}
            <p className="text-xs text-slate-400">{ct("vault_interest.rate_hint", "Paid in 52 weekly instalments, compounding each week. This is your money, so pick a rate that makes saving feel worth it.")}</p>
          </InterestDetailRow>

          {state.profiles.length === 0 ? (
            <p className="text-xs text-slate-400 border-t border-slate-100 pt-2.5">{ct("vault_interest.preview_empty", "Add a child profile to see what this will pay.")}</p>
          ) : (
            <InterestDetailRow
              id="vault-interest-cost-row"
              label={ct("vault_interest.cost_label", "Costs you")}
              value={ct("vault_interest.cost_week", "{amount} a week", { amount: fmtPence(weeklyTotal) })}
              open={costOpen}
              onToggle={() => setCostOpen((v) => !v)}
            >
              <div className="rounded-xl bg-teal-50 border border-teal-100 p-3 text-sm">
                <div className="font-semibold text-teal-800 mb-1.5">{ct("vault_interest.preview_title", "Every Monday")}</div>
                {state.profiles.map((p) => (
                  earnsVaultInterest(p) ? (
                    <div key={p.id} className="flex items-center justify-between text-teal-900 py-0.5">
                      <span className="text-sm min-w-0 truncate">{p.name} — vault {fmtPence(p.vaultBalancePence)}</span>
                      <span className="font-bold flex-shrink-0 ml-2">+{fmtPence(nextMondayPayoutPence(p, apr))}</span>
                    </div>
                  ) : (
                    <div key={p.id} className="flex items-center justify-between text-slate-400 py-0.5">
                      <span className="text-sm min-w-0 truncate">{p.name}</span>
                      <span className="text-xs flex-shrink-0 ml-2">{ct("vault_interest.preview_child_off", "not earning interest")}</span>
                    </div>
                  )
                ))}
                {eligible.length > 0 && (
                  <div className="flex items-center justify-between border-t border-teal-100 pt-1.5 mt-1.5 text-teal-900">
                    <span className="text-sm font-semibold">{ct("vault_interest.cost_year", "A year at this rate")}</span>
                    <span className="font-bold text-sm flex-shrink-0 ml-2">{fmtPence(yearlyTotal)}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mt-2">{ct("vault_interest.mechanics_note", "Interest is paid into the vault, so it compounds. Fractions of a penny carry over week to week, and any vault holding £1 or more always gets at least 1p, so Money Monday never pays nothing. Rate changes take effect from the next Monday, and every payment is logged in Recent activity.")}</p>
            </InterestDetailRow>
          )}
        </div>
      )}
    </div>
  );
}
